import { useMemo } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { ShiftManagementRolesRequirementsFragment } from 'generated/resources/graphql'
import { ShiftManagementTreeRolesDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Selector from 'modules/core/components/Selector'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import type UnassignedShiftForm from 'modules/shift_management/lib/forms/UnassignedShiftForm'

interface Props {
  form: UnassignedShiftForm
  id: number
  roles: ShiftManagementRolesRequirementsFragment[]
}

const RolesRequirements = observer(({ form, id, roles }: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const hasLegacyJobCatalogFeature = hasFeatureFlag('legacy_job_catalog')

  const { data } = useQuery(ShiftManagementTreeRolesDocument, {
    skip: hasLegacyJobCatalogFeature,
  })
  const treeRoles = useMemo(
    () => data?.jobCatalog?.treeNodesConnection?.nodes || [],
    [data?.jobCatalog?.treeNodesConnection?.nodes]
  )

  // Decide which field to use based on feature flag
  const roleField = hasLegacyJobCatalogFeature
    ? 'job_catalog_roles_ids'
    : 'job_catalog_tree_role_uuids'

  const currentValue = form.get('staff_requirements').value[id]?.[roleField] || []

  const handleSelectRoles = (ids: unknown | unknown[]) => {
    const prevRequirements = [...form.get('staff_requirements').value]

    // Clear the opposite field to maintain single source of truth during migration
    if (!hasLegacyJobCatalogFeature) {
      prevRequirements[id] = {
        ...prevRequirements[id],
        job_catalog_tree_role_uuids: ids,
        job_catalog_roles_ids: [], // Clear legacy field
      }
    } else {
      prevRequirements[id] = {
        ...prevRequirements[id],
        job_catalog_roles_ids: ids,
        job_catalog_tree_role_uuids: [], // Clear new field
      }
    }

    form.setValues({ staff_requirements: prevRequirements })
  }

  const roleOptions = !hasLegacyJobCatalogFeature
    ? treeRoles.map((role) => (
        <Selector.Option
          key={role.uuid}
          value={role.uuid}
          label={role.name || ''}
          searchValue={role.name || ''}
          isChecked={currentValue.includes(role.uuid)}
        >
          <Box width="full" overflowY="hidden">
            <Text.Normal size="200" weight="medium" ellipsis>
              {role.name || ''}
            </Text.Normal>
          </Box>
        </Selector.Option>
      ))
    : roles.map((role) => (
        <Selector.Option
          key={role.id}
          value={role.id}
          label={role.name}
          searchValue={role.name}
          isChecked={currentValue.includes(role.id)}
        >
          <Box width="full" overflowY="hidden">
            <Text.Normal size="200" weight="medium" ellipsis>
              {role.name}
            </Text.Normal>
          </Box>
        </Selector.Option>
      ))

  return (
    <Selector.Popover
      inputStyle="tags"
      label={
        id === 0
          ? i18n.t(
              'shift_management.coverage_section.unassigned_shift.staff_requirements.role_title'
            )
          : ''
      }
      blankLabel={i18n.t(
        'shift_management.coverage_section.unassigned_shift.staff_requirements.role_placeholder'
      )}
      placeholder={i18n.t(
        'shift_management.coverage_section.unassigned_shift.staff_requirements.role_placeholder'
      )}
      maxHeight="s300"
      value={currentValue}
      onChange={(value: unknown | unknown[]) => handleSelectRoles(value)}
      onToggle={(isOpen) => !isOpen}
      isMultiSelect
    >
      {roleOptions}
    </Selector.Popover>
  )
})

export { RolesRequirements }
